import { useState, type KeyboardEvent } from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * TagsInput — minimal replacement for react-tag-input.
 *
 * Behavior parity with the legacy admin's ReactTags:
 *   - Press Enter or "," to commit a tag
 *   - Click the X on a chip to remove it
 *   - Duplicate tags are dropped silently (legacy used inputFieldPosition="bottom"
 *     and `editable` — we support add + delete; in-place edit is rare and
 *     can be done by deleting + re-adding)
 *
 * Stores tags as plain strings (legacy stored {id,text} objects internally
 * but always sent the .text array to the API; here we skip the wrapper).
 */
export function TagsInput({
  value,
  onChange,
  placeholder = "Type and press Enter…",
  disabled,
}: {
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  disabled?: boolean
}) {
  const [draft, setDraft] = useState("")

  const commit = () => {
    const t = draft.trim()
    if (!t) return
    if (value.includes(t)) {
      setDraft("")
      return
    }
    onChange([...value, t])
    setDraft("")
  }

  const remove = (i: number) => {
    onChange(value.filter((_, idx) => idx !== i))
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      commit()
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      // Backspace on empty input deletes the last tag.
      remove(value.length - 1)
    }
  }

  return (
    <div
      className={cn(
        "flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 text-sm",
        disabled && "cursor-not-allowed opacity-50"
      )}
    >
      {value.map((t, i) => (
        <span
          key={`${t}-${i}`}
          className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
        >
          {t}
          <button
            type="button"
            onClick={() => remove(i)}
            disabled={disabled}
            className="rounded hover:bg-muted-foreground/20"
            aria-label={`Remove ${t}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : ""}
        disabled={disabled}
        className="min-w-[8rem] flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
      />
    </div>
  )
}
